/* eslint-disable no-mixed-spaces-and-tabs */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
	Box,
	Stack,
	Typography,
	FormHelperText,
	Button,
	CircularProgress,
	FormControlLabel,
	Switch,
	FormLabel,
} from '@mui/material';
import * as Yup from 'yup';
import { useDispatch, useSelector } from 'react-redux';
import { closeDialog } from '@/features/dialog/dialogSlice';
import { useParams } from 'react-router-dom';
import { useObjectContext } from '@/hooks/useObjectContext';
import { useSendEmailMutation } from '@/features/msGraph/msGraphApi';
import { buildOutboundEmailSharepointMetadata } from '@/features/msGraph/helper';
import { Form, Formik, useFormikContext } from 'formik';
import FormikNoteObject from '@/components/forms/fields/FormikNoteObject';
import FileUpload from '@/components/FileUpload';
import { useTheme } from '@mui/system';
import { showToast } from '@/features/toast/toastSlice';
import { triggerRefresh } from '@/features/table/attachmentsTableSlice';
import { SaveEmailDialog } from '@/components/dialogs/SaveEmailDialog';
import EmailTemplateAutofill from './EmailTemplateAutofill';
import EmailFormHeader from './EmailFormHeader';
import HtmlPreview from '@/components/HtmlPreview';
import { useMsGraph } from '@/hooks/useMsGraph';
import { SaveEmailTemplateDialog } from '@/components/dialogs/SaveEmailTemplateDialog';
import { useGetUserDetailsQuery } from '@/features/user/userApi';
import { Warning } from '@mui/icons-material';
import TiptapEditor from '@/components/TiptapEditor';
import { adjustHtmlTablePadding } from '@/utils/emailHelper';
import { FolioDrawer } from '@/components/Drawers/FolioDrawer';
import { FolioDrawerFooter } from '@/components/Drawers/FolioDrawer/FolioDrawerFooter';

const validationSchema = Yup.object().shape({
	body: Yup.string().required('Email is required.'),
	subject: Yup.string().required('Subject is required.'),
	// subject: Yup.string()
	// 	.max(SUBJECT_MAX, 'Subject should be no more than 20 characters')
	// 	.test('no-special-characters', 'Name contains invalid characters.', (value) => {
	// 		if (!value || value.trim() === '') return true;
	// 		return !hasSpecialChars(value);
	// 	}),
	recipient: Yup.array().min(1, 'Email must be sent to at least one email address'),
	id: Yup.string().required('Email must be associated with a client or deal'),
});

const ContactClientAutofill = () => {
	const { values, setFieldValue } = useFormikContext();

	useEffect(() => {
		const client = values.client;
		const attendees = client
			? values.recipient.filter((v) => v.clientId === client.clientId).concat(client)
			: [];
		setFieldValue('recipient', attendees);
	}, [values.client]);

	return <></>;
};

const EmailDataWrapper = () => {
	const theme = useTheme();

	const account = useSelector((state) => state.msalAccount.account);
	const email = account?.username;

	const { data: userDetails } = useGetUserDetailsQuery(
		{ email },
		{
			skip: !email,
		},
	);

	const [showPreview, setShowPreview] = useState(false);

	const dispatch = useDispatch();
	const params = useParams();

	const { clientRow, dealRow } = useObjectContext();

	const [showCC, setShowCC] = useState(false);
	const [showBCC, setShowBCC] = useState(false);
	const [showSaveDialog, setShowSaveDialog] = useState(false);
	const [showSaveTemplateDialog, setShowSaveTemplateDialog] = useState(false);

	const [loadingTemplate, setLoadingTemplate] = useState(false);

	const initialValues = {
		id: params.dealId ?? params.clientId,
		body: '',
		subject: '',
		recipient: [],
		cc: [],
		bcc: [],
		files: [],
		errorMessage: '',
		client: params?.clientId || clientRow || null,
		deal: params?.dealId ? dealRow : null,
		selectedFolder: '',
		driveId: '',
		template: {},
		html: '',
		from: userDetails?.displayEmail ?? email,
		delegatedEmails: userDetails?.delegatedEmails ?? [],
	};

	const [sendEmail] = useSendEmailMutation();

	const { uploadEmail } = useMsGraph();

	const handleClose = (resetForm) => {
		resetForm();
		setShowCC(false);
		setShowBCC(false);
		dispatch(closeDialog('email'));
	};

	return (
		<Formik
			{...{
				initialValues,
				validateOnChange: false,
				validateOnBlur: true,
				validationSchema,
				enableReinitialize: true,
				onSubmit: async (values, { resetForm, setFieldValue, setSubmitting }) => {
					setFieldValue('errorMessage', '');
					console.log('📋 ~ Formik values', values);
					console.log('📋 ~ Email html value', values.html);
					try {
						const images = [];
						const tempDiv = document.createElement('div');
						tempDiv.innerHTML = values.html;

						tempDiv.querySelectorAll('img').forEach((img, index) => {
							if (img.src.startsWith('data:image')) {
								images.push(img.src);
							}
						});

						const htmlValue = adjustHtmlTablePadding(values.html, values.headStyle);
						console.log('Final inner HTML', htmlValue);

						const formatRecipients = (array) =>
							array.map((c) => c.contactEmail).join(',');

						// console.log('Here is the end result html', tempDiv.innerHTML);

						const sendResult = await sendEmail({
							recipient: formatRecipients(values.recipient),
							...(values.cc.length > 0 && { cc: formatRecipients(values.cc) }),
							...(values.bcc.length > 0 && { bcc: formatRecipients(values.bcc) }),
							subject: values.subject.trim(),
							body: htmlValue.replace('\r\n', ''),
							from: values.from?.name,
							...(images.length > 0 && { images }),
							...(values.files.length > 0 && { files: values.files }),
							email: email,
						}).unwrap();
						const messageId = sendResult?.messageId ?? sendResult;
						console.log('Here is the send result', sendResult);
						if (messageId) {
							dispatch(
								showToast({
									message: 'Email sent successfully!',
									autohide: true,
								}),
							);
						}
						if (values.selectedFolder && values.driveId && messageId) {
							const { driveId, selectedFolder, subject } = values;
							const uploadResult = await uploadEmail({
								messageId,
								sendResult,
								driveId,
								selectedFolder,
								subject: subject.trim(),
								metadata: buildOutboundEmailSharepointMetadata({
									sendResult,
									values,
									userDetails,
								}),
							});

							if (uploadResult.success) {
								dispatch(triggerRefresh());
							}
						}

						handleClose(resetForm);
					} catch (err) {
						console.log('🙅 ~ Error creating email', err);
						setFieldValue(
							'errorMessage',
							`Error creating email: ${err?.message ?? ''}`,
						);
					}
					setSubmitting(false);
				},
			}}
		>
			{({
				isSubmitting,
				setFieldValue,
				setFieldError,
				setFieldTouched,
				values,
				dirty,
				handleSubmit,
				handleChange,
				touched,
				errors,
				getFieldProps,
				resetForm,
				validateField,
				validateForm,
				setErrors,
				setTouched,
			}) => {
				const clientLoad =
					!values.client && (values?.template?.requirements ?? []).includes('client');
				const dealLoad =
					!values.deal && (values?.template?.requirements ?? []).includes('deal');
				const loadingFromTemplateScreen =
					!loadingTemplate && values.template?.id && (clientLoad || dealLoad);

				return (
					<Form>
						<FolioDrawer
							open
							onClose={() => handleClose(resetForm)}
							confirmClose={dirty}
							canMaximise
							title='Compose Email'
						>
							<Stack direction='column' spacing={2}>
								<FormikNoteObject />
								<ContactClientAutofill />
								<EmailTemplateAutofill setLoadingTemplate={setLoadingTemplate} />
								<EmailFormHeader
									{...{
										showCC,
										setShowCC,
										showBCC,
										setShowBCC,
										setLoadingTemplate,
									}}
								/>
								<Box display='flex' justifyContent={'flex-end'}>
									<FormControlLabel
										control={
											<Switch
												checked={showPreview}
												disabled={loadingTemplate}
												onChange={(e) => setShowPreview(e.target.checked)}
											/>
										}
										label={showPreview ? 'Edit email body' : 'Show preview'}
									/>
								</Box>
								{loadingFromTemplateScreen ? (
									<Box
										display='flex'
										justifyContent={'center'}
										alignItems={'center'}
										width='100%'
										height='6em'
									>
										<Stack direction={'row'} spacing={1}>
											<Warning
												sx={(theme) => ({ color: theme.palette.text.secondary })}
											/>
											<Typography>
												Please select a client and deal to use this template.
											</Typography>
										</Stack>
									</Box>
								) : loadingTemplate ? (
									<Box
										display='flex'
										justifyContent={'center'}
										alignItems={'center'}
										width='100%'
										height='6em'
									>
										<CircularProgress />
									</Box>
								) : showPreview ? (
									<HtmlPreview htmlValue={values.html} />
								) : (
									<TiptapEditor
										value={values.html}
										onChange={(html, content) => {
											// console.log('TIPTAP ON CHANGE', params);
											setFieldValue('html', html);
											setFieldValue('body', content.trim());
										}}
										onBlur={async () => {
											await validateField('body');
										}}
										error={touched.body && Boolean(errors.body)}
										disabled={isSubmitting}
										config={{
											toolbar: {
												user: {
													showSignature: true,
													show: true,
												},
												brokerage: { show: true },
												deal: {
													dealId: values.deal?.dealId,
													show: Boolean(values?.deal),
												},
												presetTable: {
													show: Boolean(values?.deal) && Boolean(values?.client),
												},
												templateType: values.template?.type,
												client: {
													clientId:
														values.client?.clientId ?? values.client?.hubspotId,
													show: Boolean(values?.client),
												},
												riskInfo: {
													show: false,
												},
												containerBorder: {
													show: true,
												},
												align: { show: true },
												image: { show: true },
												table: { show: true },
											},
										}}
									/>
								)}

								{touched.body && Boolean(errors.body) && (
									<FormHelperText error>{touched.body && errors.body}</FormHelperText>
								)}
								<FileUpload
									files={values.files}
									setFiles={(files) =>
										handleChange({
											target: {
												name: 'files',
												value: files,
											},
										})
									}
									loading={isSubmitting}
								/>
								{values.errorMessage.length > 0 && (
									<FormHelperText error>{values.errorMessage}</FormHelperText>
								)}
							</Stack>
							<FolioDrawerFooter>
								<Stack direction='row' spacing={1}>
									<Button
										color='primary'
										// type='submit'
										variant='contained'
										disabled={isSubmitting || loadingTemplate}
										onClick={(e) => {
											validateForm().then((errorValues) => {
												if (Object.keys(errorValues).length === 0) {
													setShowSaveDialog(true);
												} else {
													const touched = Object.entries(errorValues).reduce(
														(acc, [key, val]) => {
															acc[key] = Boolean(val);
															return acc;
														},
														{},
													);
													setTouched(touched);
													setErrors(errorValues);
													const errorFields = document.getElementsByClassName(
														'Mui-error MuiFormLabel-root',
													);
													setTimeout(() => {
														errorFields?.[errorFields.length - 1]?.scrollIntoView();
													}, 0);
												}
											});
										}}
									>
										{isSubmitting ? (
											<CircularProgress size='2em' sx={{ color: '#ffffff' }} />
										) : (
											'Send Email'
										)}
									</Button>
									<Button
										color='primary'
										// type='submit'
										variant='outlined'
										sx={{
											borderRadius:
												theme.components?.MuiButton?.styleOverrides
													?.containedPrimary?.borderRadius,
										}}
										disabled={isSubmitting || loadingTemplate}
										onClick={(e) => {
											e.preventDefault();
											validateForm().then((values) => {
												if (!values?.body) {
													setShowSaveTemplateDialog(true);
												} else {
													setFieldTouched('body', true, true);
												}
											});
										}}
									>
										{isSubmitting ? (
											<CircularProgress size='2em' sx={{ color: '#ffffff' }} />
										) : (
											'Save Template'
										)}
									</Button>
								</Stack>
							</FolioDrawerFooter>
						</FolioDrawer>
						{showSaveDialog && (
							<SaveEmailDialog
								{...{
									open: showSaveDialog,
									handleClose: () => setShowSaveDialog(false),
									handleSave: () => {
										setShowSaveDialog(false);
										handleSubmit();
									},
								}}
							/>
						)}
						{showSaveTemplateDialog && (
							<SaveEmailTemplateDialog
								{...{
									open: showSaveTemplateDialog,
									handleClose: () => setShowSaveTemplateDialog(false),
									htmlValue: values.html,
									headContent: values.headStyle,
									templateType: values.template?.type ?? '',
									existingTemplate: values.template,
								}}
							/>
						)}
					</Form>
				);
			}}
		</Formik>
	);
};

export const AddEmailDrawer = () => {
	const dialogData = useSelector((state) => state.dialog);
	const open = dialogData?.open?.email ?? false;

	if (!open) return <></>;
	return <EmailDataWrapper />;
};
